import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
    getAdminOrManagerRoles,
    getCanApproveSelectedOrder,
    getCanMoveToPendingApprovalSelectedOrder,
    getCanMoveToSupervisorReviewSelectedOrder,
} from 'entities/Order/model/selectors/ordersPermissions';
import { getSelectedOrder } from 'entities/Order/model/selectors/ordersSelectors';
import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
// eslint-disable-next-line max-len
import { getProposalFilterSellersDataBySeller } from 'features/OrderProposalFilter/model/selectors/orderProposalFilteSelectors';
import {
    ApproveOrderButton,
    MoveToApprovalOrderButton,
    MoveToPendingOrderButton,
} from 'features/changeOrderStatus';

interface OrderViewPageApproveButtonProps {
    isNoItemsInOrder: boolean;
    canBeApproved: boolean;
    hasLoadingPnA: boolean;
}

export const OrderViewPageApproveButton = ({
    isNoItemsInOrder,
    canBeApproved,
    hasLoadingPnA,
}: OrderViewPageApproveButtonProps) => {
    const params = useParams();
    const canApprove = useSelector(getCanApproveSelectedOrder);
    const currentProperty = useSelector(getSelectedProperty);
    const currentOrder = useSelector(getSelectedOrder);
    const sellersInProposals = useSelector(
        getProposalFilterSellersDataBySeller,
    );

    const canMoveToPendingApproval = useSelector(
        getCanMoveToPendingApprovalSelectedOrder,
    );

    const canMoveToSupervisor = useSelector(
        getCanMoveToSupervisorReviewSelectedOrder,
    );
    const isManagerOrPMC = useSelector(getAdminOrManagerRoles);

    const isRequiredPoInProperty = useMemo(
        () => !!currentProperty?.isPoNumbersRequired,
        [currentProperty],
    );

    const isRequiredPoNumberForOrder = useMemo(() => {
        const poNumbers = currentOrder?.poNumbers;
        let disabledApproveOrder = false;
        Object.entries(sellersInProposals).forEach(([, sellerData]) => {
            const sellerUid = sellerData?.sellerUid;
            const matchingPo = poNumbers.find((el) => el.id === sellerUid);
            if (!matchingPo?.poNumber) {
                disabledApproveOrder = true;
            }
        });
        return isRequiredPoInProperty && disabledApproveOrder && isManagerOrPMC;
    }, [
        currentOrder,
        isRequiredPoInProperty,
        sellersInProposals,
        isManagerOrPMC,
    ]);

    const orderId = params?.orderId;

    if (canApprove) {
        return (
            <ApproveOrderButton
                orderId={orderId}
                isNoItemsInOrder={isNoItemsInOrder || !canBeApproved}
                isRequiredPoNumberForOrder={isRequiredPoNumberForOrder}
                hasLoadingPnA={hasLoadingPnA}
            />
        );
    }

    if (canMoveToPendingApproval) {
        return (
            <MoveToPendingOrderButton
                orderId={orderId}
                isNoItemsInOrder={isNoItemsInOrder}
            />
        );
    }

    if (canMoveToSupervisor) {
        return (
            <MoveToApprovalOrderButton
                orderId={orderId}
                isNoItemsInOrder={isNoItemsInOrder}
            />
        );
    }

    return null;
};
